/**
 * CasaMapa "Recommended" ranking: quiz/account prefs + in-budget sponsored boost
 * + attribute completeness priority.
 */

import { mapCasaMapaQuizAnswersToHousingPrefs } from "./casaMapaQuizMapper";
import {
  loadCasaMapaQuizProgress,
  loadCasaMapaQuizResult,
} from "./casaMapaQuizProgress";
import { normalizeCasaMapaMunicipality } from "./casaMapaMunicipality";
import { resolveCasaMapaPriceAmount } from "./casaMapaListingDetailFormat";

export const CASAMAPA_SEARCH_SPONSORED_SLOT_KEY = "search-inline-sponsored-listing";

/** Core public attributes that make a listing useful in search + detail. */
const ATTRIBUTE_COMPLETENESS_CHECKS = Object.freeze([
  {
    key: "price",
    weight: 35,
    test: (listing) => resolveCasaMapaPriceAmount(listing) != null,
  },
  {
    key: "bedrooms",
    weight: 45,
    test: (listing) =>
      listing?.propertyType === "lot" ||
      listing?.propertyType === "studio" ||
      listing?.bedrooms != null,
  },
  {
    key: "bathrooms",
    weight: 45,
    test: (listing) => listing?.propertyType === "lot" || listing?.bathrooms != null,
  },
  {
    key: "constructedArea",
    weight: 50,
    test: (listing) =>
      listing?.constructedAreaM2 != null ||
      listing?.area != null ||
      listing?.privateAreaM2 != null ||
      listing?.lotAreaM2 != null,
  },
  {
    key: "parking",
    weight: 30,
    test: (listing) => listing?.parkingSpaces != null || listing?.parking != null,
  },
  {
    key: "propertyType",
    weight: 20,
    test: (listing) => Boolean(listing?.propertyType && listing.propertyType !== "unknown"),
  },
  {
    key: "listingType",
    weight: 15,
    test: (listing) => Boolean(listing?.listingType && listing.listingType !== "unknown"),
  },
  {
    key: "location",
    weight: 25,
    test: (listing) =>
      Boolean(
        listing?.location?.municipality?.name ||
          listing?.municipality ||
          listing?.location?.neighborhood?.name ||
          listing?.neighborhood ||
          listing?.location?.displayLabel
      ),
  },
  {
    key: "address",
    weight: 30,
    test: (listing) =>
      Boolean(
        listing?.address?.display ||
          listing?.address?.street ||
          listing?.addressText ||
          listing?.streetAddress
      ),
  },
  {
    key: "images",
    weight: 25,
    test: (listing) =>
      (listing?.imageCount || listing?.photoCount || 0) > 0 ||
      Boolean(listing?.primaryImage || listing?.primaryImageUrl || listing?.thumbnailUrl),
  },
  {
    key: "estrato",
    weight: 10,
    test: (listing) => listing?.estrato != null && listing.estrato >= 1 && listing.estrato <= 6,
  },
  {
    key: "yearBuilt",
    weight: 10,
    test: (listing) => listing?.yearBuilt != null && listing.yearBuilt >= 1800,
  },
  {
    key: "description",
    weight: 15,
    test: (listing) => Boolean(listing?.description || listing?.shortDescription),
  },
]);

function municipalityKey(value) {
  return String(normalizeCasaMapaMunicipality(value) || value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Score how completely a listing's buyer-facing attributes are populated.
 * Prefer server dataQuality when present, then weight core display fields.
 */
export function scoreCasaMapaListingAttributeCompleteness(listing = {}) {
  let score = 0;
  let filledWeight = 0;
  let totalWeight = 0;

  for (const field of ATTRIBUTE_COMPLETENESS_CHECKS) {
    totalWeight += field.weight;
    if (field.test(listing)) {
      filledWeight += field.weight;
      score += field.weight;
    }
  }

  const dataQuality = listing?.dataQuality || listing?.trustContext?.completeness || null;
  if (
    dataQuality?.availableFieldCount != null &&
    dataQuality?.expectedFieldCount != null &&
    dataQuality.expectedFieldCount > 0
  ) {
    const ratio = Number(dataQuality.availableFieldCount) / Number(dataQuality.expectedFieldCount);
    if (Number.isFinite(ratio) && ratio > 0) {
      // Soft alignment with public completeness methodology (not verification).
      score += Math.round(Math.min(1, ratio) * 60);
    }
  }

  return {
    score,
    filledWeight,
    totalWeight,
    ratio: totalWeight > 0 ? filledWeight / totalWeight : 0,
  };
}

/**
 * Pull listing ids from public sponsorship placements.
 */
export function extractSponsoredListingIds(placements = []) {
  const ids = new Set();
  const rows = Array.isArray(placements) ? placements : [];
  rows.forEach((placement) => {
    const direct =
      placement?.metadata?.listingId ||
      placement?.associatedListingId ||
      placement?.listingId;
    if (direct) {
      ids.add(String(direct).trim());
    }
    const href = String(placement?.targetUrl || "");
    const match = href.match(/\/listings\/([^/?#]+)/i);
    if (match?.[1]) {
      ids.add(decodeURIComponent(match[1]).trim());
    }
  });
  return [...ids].filter(Boolean);
}

/**
 * Resolve preference signals for recommended sort.
 * Precedence: completed quiz result → in-progress quiz → account housing prefs → active search filters.
 */
export function resolveCasaMapaRecommendationProfile({
  slug,
  searchState = {},
  housingPrefs = null,
} = {}) {
  const quizResult = slug ? loadCasaMapaQuizResult(slug) : null;
  const quizProgress = slug ? loadCasaMapaQuizProgress(slug) : null;
  const quizAnswers = quizResult?.answers || quizProgress?.answers || null;

  const fromQuiz = quizAnswers?.listingType
    ? mapCasaMapaQuizAnswersToHousingPrefs(quizAnswers)
    : null;
  const account = housingPrefs && typeof housingPrefs === "object" ? housingPrefs : null;

  const municipalities = (() => {
    if (Array.isArray(account?.municipalities) && account.municipalities.length) {
      return account.municipalities.map(normalizeCasaMapaMunicipality).filter(Boolean);
    }
    if (Array.isArray(fromQuiz?.municipalities) && fromQuiz.municipalities.length) {
      return fromQuiz.municipalities.map(normalizeCasaMapaMunicipality).filter(Boolean);
    }
    if (Array.isArray(searchState.municipalities) && searchState.municipalities.length) {
      return searchState.municipalities.map(normalizeCasaMapaMunicipality).filter(Boolean);
    }
    if (searchState.municipality) {
      const one = normalizeCasaMapaMunicipality(searchState.municipality);
      return one ? [one] : [];
    }
    return [];
  })();

  const propertyTypes = (() => {
    if (Array.isArray(account?.propertyTypes) && account.propertyTypes.length) {
      return account.propertyTypes.filter(Boolean);
    }
    if (Array.isArray(fromQuiz?.propertyTypes) && fromQuiz.propertyTypes.length) {
      return fromQuiz.propertyTypes.filter(Boolean);
    }
    if (Array.isArray(searchState.propertyTypes) && searchState.propertyTypes.length) {
      return searchState.propertyTypes.filter(Boolean);
    }
    return [];
  })();

  const listingType =
    (account?.intent && account.intent !== "both" ? account.intent : null) ||
    fromQuiz?.intent ||
    searchState.listingType ||
    null;

  const budgetMaxCop =
    toNumber(account?.budgetMaxCop) ??
    toNumber(fromQuiz?.budgetMaxCop) ??
    toNumber(searchState.priceMaxCop);

  const budgetMinCop =
    toNumber(account?.budgetMinCop) ??
    toNumber(fromQuiz?.budgetMinCop) ??
    toNumber(searchState.priceMinCop);

  return {
    source: quizResult?.answers
      ? "quiz_result"
      : quizAnswers?.listingType
        ? "quiz_progress"
        : account
          ? "account"
          : "search",
    listingType,
    budgetMaxCop,
    budgetMinCop,
    propertyTypes,
    bedroomsMin:
      toNumber(account?.bedroomsMin) ??
      toNumber(fromQuiz?.bedroomsMin) ??
      toNumber(searchState.bedroomsMin),
    bathroomsMin:
      toNumber(account?.bathroomsMin) ??
      toNumber(fromQuiz?.bathroomsMin) ??
      toNumber(searchState.bathroomsMin),
    parkingMin:
      toNumber(account?.parkingMin) ??
      toNumber(fromQuiz?.parkingMin) ??
      toNumber(searchState.parkingMin),
    constructedAreaMinM2: toNumber(searchState.constructedAreaMinM2),
    municipalities,
    lifestyles: Array.isArray(quizAnswers?.lifestyles) ? quizAnswers.lifestyles : [],
  };
}

export function listingPriceWithinBudget(listing, profile = {}) {
  const price = resolveCasaMapaPriceAmount(listing);
  if (price == null) {
    return false;
  }
  if (profile.budgetMinCop != null && price < profile.budgetMinCop) {
    return false;
  }
  if (profile.budgetMaxCop != null && price > profile.budgetMaxCop) {
    return false;
  }
  return true;
}

/**
 * Higher score = higher in Recommended results.
 * Sponsored listings only get priority when they fit the user's budget.
 */
export function scoreCasaMapaListingRecommendation(
  listing,
  profile = {},
  { sponsoredIdSet = null } = {}
) {
  let score = 0;
  const id = String(listing?.id || "").trim();
  const price = resolveCasaMapaPriceAmount(listing);
  const isSponsored = Boolean(id && sponsoredIdSet?.has(id));
  const hasBudgetCap = profile.budgetMaxCop != null || profile.budgetMinCop != null;
  const inBudget = hasBudgetCap
    ? listingPriceWithinBudget(listing, profile)
    : price != null;

  if (isSponsored && inBudget) {
    score += 10000;
  }

  if (hasBudgetCap) {
    if (inBudget) {
      score += 400;
      if (price != null && profile.budgetMaxCop) {
        const ratio = price / profile.budgetMaxCop;
        if (ratio >= 0.45 && ratio <= 1) {
          score += Math.round((1 - Math.abs(ratio - 0.8)) * 80);
        }
      }
    } else if (price != null) {
      score -= 250;
    }
  }

  if (profile.listingType && listing?.listingType === profile.listingType) {
    score += 180;
  }

  const listingMuni =
    listing?.municipality ||
    listing?.location?.municipality?.name ||
    listing?.city ||
    listing?.location?.city;
  if (profile.municipalities?.length && listingMuni) {
    const listingKey = municipalityKey(listingMuni);
    if (profile.municipalities.some((m) => municipalityKey(m) === listingKey)) {
      score += 320;
    }
  }

  if (profile.propertyTypes?.length && listing?.propertyType) {
    if (profile.propertyTypes.includes(listing.propertyType)) {
      score += 260;
    }
  }

  if (profile.bedroomsMin != null && listing?.bedrooms != null) {
    score += listing.bedrooms >= profile.bedroomsMin ? 140 : -60;
  }
  if (profile.bathroomsMin != null && listing?.bathrooms != null) {
    score += listing.bathrooms >= profile.bathroomsMin ? 90 : -40;
  }
  if (profile.parkingMin != null && listing?.parkingSpaces != null) {
    score += listing.parkingSpaces >= profile.parkingMin ? 80 : -30;
  } else if (profile.lifestyles?.includes("parking") && (listing?.parkingSpaces || 0) >= 1) {
    score += 60;
  }

  const area =
    listing?.constructedAreaM2 ?? listing?.area ?? listing?.privateAreaM2 ?? null;
  if (profile.constructedAreaMinM2 != null && area != null) {
    score += area >= profile.constructedAreaMinM2 ? 70 : -30;
  } else if (profile.lifestyles?.includes("more_space") && area != null && area >= 80) {
    score += 50;
  }

  // Prefer listings with more populated buyer-facing attributes.
  score += scoreCasaMapaListingAttributeCompleteness(listing).score;

  return score;
}

/**
 * Stable recommended sort. Marks matching sponsored rows for UI disclosure.
 */
export function sortCasaMapaListingsRecommended(
  listings = [],
  profile = {},
  { sponsoredIds = [] } = {}
) {
  const sponsoredIdSet = new Set(
    (Array.isArray(sponsoredIds) ? sponsoredIds : []).map((id) => String(id).trim()).filter(Boolean)
  );
  const scored = (Array.isArray(listings) ? listings : []).map((listing, index) => {
    const id = String(listing?.id || "").trim();
    const isSponsored = Boolean(id && sponsoredIdSet.has(id));
    const hasBudgetCap = profile.budgetMaxCop != null || profile.budgetMinCop != null;
    const inBudget = hasBudgetCap
      ? listingPriceWithinBudget(listing, profile)
      : resolveCasaMapaPriceAmount(listing) != null;
    const completeness = scoreCasaMapaListingAttributeCompleteness(listing);
    const score = scoreCasaMapaListingRecommendation(listing, profile, { sponsoredIdSet });
    return {
      listing: {
        ...listing,
        sponsored: isSponsored && inBudget,
        recommendationScore: score,
        attributeCompletenessRatio: completeness.ratio,
      },
      score,
      completenessScore: completeness.score,
      index,
    };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    if (b.completenessScore !== a.completenessScore) {
      return b.completenessScore - a.completenessScore;
    }
    return a.index - b.index;
  });

  return scored.map((row) => row.listing);
}

/**
 * Map CasaMapa recommended sort to an API-supported sort key.
 */
export function toCasaMapaApiSort(sort, { q = null } = {}) {
  if (sort === "recommended") {
    return q ? "relevance" : "recent";
  }
  return sort || (q ? "relevance" : "recent");
}
